import React, { useState, useEffect } from "react";
import {
  X,
  Calendar,
  DollarSign,
  FileText,
  CheckCircle,
  Building,
  Hammer,
  Receipt,
  Edit3,
} from "lucide-react";
import axios from "axios";
import { MyDomain } from "../../../App";

const expenseTypes = [
  { id: 1, name: "Materials", color: "#EF4444", bg: "#FEF2F2" },
  { id: 2, name: "Equipment", color: "#8B5CF6", bg: "#F5F3FF" },
  { id: 3, name: "Transportation", color: "#3B82F6", bg: "#EFF6FF" },
  { id: 4, name: "Utilities", color: "#F59E0B", bg: "#FFFBEB" },
  { id: 5, name: "Other", color: "#6B7280", bg: "#F9FAFB" },
];

// ── Editable invoice field row ───────────────────────────────────────────────
function EditableField({
  label,
  value,
  onChange,
  multiline = false,
  highlight = false,
  type = "text",
}) {
  return (
    <div className="flex items-start gap-3">
      <label className="text-xs font-semibold text-yellow-800 w-32 flex-shrink-0 pt-2">
        {label}:
      </label>
      {multiline ? (
        <textarea
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          rows={2}
          className={`flex-1 bg-white border rounded-lg px-3 py-2 text-sm text-gray-900 outline-none resize-none focus:ring-1 ${
            highlight
              ? "border-yellow-400 border-2 font-bold focus:ring-yellow-400"
              : "border-yellow-200 focus:ring-yellow-300"
          }`}
        />
      ) : (
        <input
          type={type}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className={`flex-1 bg-white border rounded-lg px-3 py-2 text-sm text-gray-900 outline-none focus:ring-1 ${
            highlight
              ? "border-yellow-400 border-2 font-bold focus:ring-yellow-400"
              : "border-yellow-200 focus:ring-yellow-300"
          }`}
        />
      )}
    </div>
  );
}

export default function BossUpdateExpensesModal({
  visible,
  onClose,
  expense,
  onUpdateSuccess,
}) {
  const [allData, setAllData] = useState(null);

  const [selectedPlaceId, setSelectedPlaceId] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [title, setTitle] = useState("");
  const [price, setPrice] = useState("");
  const [date, setDate] = useState("");
  const [note, setNote] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Invoice states
  const [hasInvoice, setHasInvoice] = useState(false);
  const [invoiceData, setInvoiceData] = useState(null);

  // Load allData from localStorage
  useEffect(() => {
    const data = localStorage.getItem("allData");
    if (data) setAllData(JSON.parse(data));
  }, []);

  // Populate fields when modal opens
  useEffect(() => {
    if (visible && expense && allData) {
      setTitle(expense.title || "");
      setPrice(expense.price?.toString() || "");
      setDate(expense.date || "");
      setNote(expense.note || "");
      setSelectedType(expense.type);
      setSelectedPlaceId(expense.placeId);
      setHasInvoice(expense.withInvoice === 1);

      if (expense.withInvoice === 1) {
        const invoice = (allData.myInvoices || []).find(
          (inv) => inv.expensesId === expense.id,
        );
        if (invoice) {
          setInvoiceData({
            invoiceNumber: invoice.inoviceNumber || "",
            invoiceDate: invoice.inoviceDate || "",
            vendorName: invoice.vendorName || "",
            businessType: invoice.businessType || "",
            businessNumber: invoice.businessNumber || "",
            subtotal: invoice.subTotal || "",
            taxAmount: invoice.taxAmount || "",
            taxRate: invoice.taxRate || "",
            totalAmount: invoice.totalAmount || "",
          });
        }
      }
    }
  }, [visible, expense, allData]);

  const handleClose = () => {
    setSelectedPlaceId(null);
    setSelectedType(null);
    setTitle("");
    setPrice("");
    setDate("");
    setNote("");
    setHasInvoice(false);
    setInvoiceData(null);
    onClose();
  };

  const updateInvoiceField = (field, value) => {
    setInvoiceData((prev) => ({ ...prev, [field]: value }));
  };

  const handleUpdateExpense = async () => {
    if (!date || date.trim() === "") {
      alert("Please enter a date");
      return;
    }
    if (
      !price ||
      price.trim() === "" ||
      isNaN(price) ||
      parseFloat(price) <= 0
    ) {
      alert("Please enter a valid price");
      return;
    }
    if (!selectedType) {
      alert("Please select an expense type");
      return;
    }

    setIsLoading(true);
    try {
      const requestData = {
        Id: expense.id,
        Title: title.trim(),
        Note: note.trim(),
        Date: date,
        Type: selectedType,
        Price: parseInt(price),
        WhoMake: expense.whoMake,
        PlaceId: selectedPlaceId || null,
      };

      // Only include invoice data if expense has invoice
      if (hasInvoice && invoiceData) {
        requestData.InvoiceData = {
          InvoiceNumber: invoiceData.invoiceNumber || "",
          InvoiceDate: invoiceData.invoiceDate || "",
          VendorName: invoiceData.vendorName || "",
          BusinessType: invoiceData.businessType || "",
          BusinessNumber: invoiceData.businessNumber || "",
          SubTotal: invoiceData.subtotal || "",
          TaxAmount: invoiceData.taxAmount || "",
          TaxRate: invoiceData.taxRate?.replace("%", "") || "",
          TotalAmount: invoiceData.totalAmount || "",
        };
      }

      const response = await axios.put(
        `${MyDomain}Expenses/UpdateExpenses`,
        requestData,
        { headers: { "Content-Type": "application/json" } },
      );

      if (response.status === 200) {
        // Update localStorage
        const currentData = JSON.parse(localStorage.getItem("allData") || "{}");
        const updatedExpenses = (currentData.myExpenses || []).map((exp) =>
          exp.id === expense.id
            ? {
                ...exp,
                title: title.trim(),
                note: note.trim(),
                date,
                type: selectedType,
                price: parseInt(price),
                placeId: selectedPlaceId,
              }
            : exp,
        );

        const updatedInvoices =
          hasInvoice && response.data?.Invoice
            ? (currentData.myInvoices || []).map((inv) =>
                inv.expensesId === expense.id
                  ? {
                      ...inv,
                      inoviceNumber: response.data.Invoice.InoviceNumber,
                      inoviceDate: response.data.Invoice.InoviceDate,
                      vendorName: response.data.Invoice.VendorName,
                      businessType: response.data.Invoice.BusinessType,
                      businessNumber: response.data.Invoice.BusinessNumber,
                      subTotal: response.data.Invoice.SubTotal,
                      taxAmount: response.data.Invoice.TaxAmount,
                      taxRate: response.data.Invoice.TaxRate,
                      totalAmount: response.data.Invoice.TotalAmount,
                    }
                  : inv,
              )
            : currentData.myInvoices;

        const updated = {
          ...currentData,
          myExpenses: updatedExpenses,
          myInvoices: updatedInvoices,
        };
        localStorage.setItem("allData", JSON.stringify(updated));

        handleClose();
        if (onUpdateSuccess) onUpdateSuccess();
        alert(
          hasInvoice
            ? "Expense and invoice updated successfully"
            : "Expense updated successfully",
        );
      }
    } catch (error) {
      console.error(
        "❌ Error updating expense:",
        error.response?.data || error.message,
      );
      alert(error.response?.data?.message || "Failed to update expense");
    } finally {
      setIsLoading(false);
    }
  };

  if (!visible || !expense || !allData) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-end sm:items-center justify-center z-50">
      <div
        className="bg-white rounded-t-3xl sm:rounded-3xl w-full max-w-2xl max-h-[92vh] flex flex-col animate-slide-up"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Header ── */}
        <div className="border-b border-gray-100 px-5 pt-3 pb-4 rounded-t-3xl">
          <div className="w-9 h-1 bg-gray-300 rounded-full mx-auto mb-4 sm:hidden" />
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                <Edit3 className="w-6 h-6 text-yellow-500" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  Update Expense
                </h2>
                <p className="text-sm text-gray-500">
                  {hasInvoice ? "With Invoice" : "Edit expense details"}
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>
          </div>
        </div>

        {/* ── Scrollable form ── */}
        <div className="flex-1 overflow-y-auto px-5 py-5">
          <div className="space-y-5">
            {/* Date */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Date <span className="text-red-500">*</span>
              </label>
              <div className="flex items-center bg-gray-50 border-2 border-gray-200 rounded-xl px-4 py-3 focus-within:border-yellow-400 transition">
                <Calendar className="w-5 h-5 text-gray-400 mr-3 flex-shrink-0" />
                <input
                  type="text"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  placeholder="DD-MM-YYYY"
                  maxLength={10}
                  className="flex-1 bg-transparent outline-none text-gray-900"
                />
              </div>
            </div>

            {/* Expense Type */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Expense Type <span className="text-red-500">*</span>
              </label>
              <div className="overflow-x-auto pb-1 hide-scrollbar">
                <div className="flex gap-3" style={{ width: "max-content" }}>
                  {expenseTypes.map((type) => {
                    const isActive = selectedType === type.id;
                    return (
                      <button
                        key={type.id}
                        onClick={() => setSelectedType(type.id)}
                        className="relative w-28 flex flex-col items-center gap-2 p-3 rounded-xl border-2 transition"
                        style={{
                          borderColor: isActive ? type.color : "#E5E7EB",
                          backgroundColor: isActive ? type.bg : "#F9FAFB",
                        }}
                      >
                        {isActive && (
                          <CheckCircle
                            className="absolute top-2 right-2 w-4 h-4"
                            style={{ color: type.color }}
                          />
                        )}
                        <div
                          className="w-12 h-12 rounded-full flex items-center justify-center"
                          style={{ backgroundColor: `${type.color}20` }}
                        >
                          <Receipt
                            className="w-6 h-6"
                            style={{ color: type.color }}
                          />
                        </div>
                        <span
                          className="text-xs font-semibold text-center"
                          style={{ color: isActive ? type.color : "#374151" }}
                        >
                          {type.name}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Title */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Title
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter title"
                maxLength={100}
                className="w-full bg-gray-50 border-2 border-gray-200 rounded-xl px-4 py-3 text-gray-900 outline-none focus:border-yellow-400 transition"
              />
            </div>

            {/* Price */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Price <span className="text-red-500">*</span>
              </label>
              <div className="flex items-center bg-gray-50 border-2 border-gray-200 rounded-xl px-4 py-3 focus-within:border-yellow-400 transition">
                <DollarSign className="w-5 h-5 text-gray-400 mr-3 flex-shrink-0" />
                <input
                  type="number"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  placeholder="Enter amount"
                  className="flex-1 bg-transparent outline-none text-gray-900"
                />
                <span className="text-gray-500 font-semibold ml-2">₪</span>
              </div>
            </div>

            {/* Invoice section — only shown when expense.withInvoice === 1 */}
            {hasInvoice && invoiceData && (
              <div className="bg-yellow-50 border-2 border-yellow-400 rounded-xl p-4 space-y-3">
                <div className="flex items-center gap-2 mb-1">
                  <FileText className="w-5 h-5 text-yellow-500" />
                  <h3 className="text-base font-bold text-yellow-900">
                    Invoice Details
                  </h3>
                </div>

                <EditableField
                  label="Invoice Number"
                  value={invoiceData.invoiceNumber}
                  onChange={(v) => updateInvoiceField("invoiceNumber", v)}
                />
                <EditableField
                  label="Invoice Date"
                  value={invoiceData.invoiceDate}
                  onChange={(v) => updateInvoiceField("invoiceDate", v)}
                />
                <EditableField
                  label="Vendor Name"
                  value={invoiceData.vendorName}
                  onChange={(v) => updateInvoiceField("vendorName", v)}
                  multiline
                />
                <EditableField
                  label="Business Type"
                  value={invoiceData.businessType}
                  onChange={(v) => updateInvoiceField("businessType", v)}
                />
                <EditableField
                  label="Business Number"
                  value={invoiceData.businessNumber}
                  onChange={(v) => updateInvoiceField("businessNumber", v)}
                />
                <EditableField
                  label="Subtotal"
                  value={invoiceData.subtotal}
                  onChange={(v) => updateInvoiceField("subtotal", v)}
                  type="number"
                />
                <EditableField
                  label="Tax Amount"
                  value={invoiceData.taxAmount}
                  onChange={(v) => updateInvoiceField("taxAmount", v)}
                  type="number"
                />
                <EditableField
                  label="Tax Rate"
                  value={invoiceData.taxRate}
                  onChange={(v) => updateInvoiceField("taxRate", v)}
                  type="number"
                />
                <EditableField
                  label="Total Amount"
                  value={invoiceData.totalAmount}
                  onChange={(v) => updateInvoiceField("totalAmount", v)}
                  type="number"
                  highlight
                />
              </div>
            )}

            {/* Place Selection */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Place (Optional)
              </label>

              {(allData.myPlaces || []).length === 0 ? (
                <div className="flex flex-col items-center justify-center py-10 bg-gray-50 rounded-xl border border-dashed border-gray-300">
                  <Building className="w-10 h-10 text-gray-300 mb-2" />
                  <p className="text-sm text-gray-400">No places found</p>
                </div>
              ) : (
                <div className="overflow-x-auto pb-1 hide-scrollbar">
                  <div className="flex gap-3" style={{ width: "max-content" }}>
                    {(allData.myPlaces || []).map((place) => {
                      const isActive = selectedPlaceId === place.id;
                      return (
                        <button
                          key={place.id}
                          onClick={() => setSelectedPlaceId(place.id)}
                          className={`relative w-32 flex flex-col items-center gap-2 p-3 rounded-xl border-2 transition ${
                            isActive
                              ? "bg-yellow-50 border-yellow-400"
                              : "bg-gray-50 border-gray-200 hover:border-gray-300"
                          }`}
                        >
                          {isActive && (
                            <CheckCircle className="absolute top-2 right-2 w-4 h-4 text-yellow-500" />
                          )}
                          <div
                            className={`w-12 h-12 rounded-full flex items-center justify-center ${
                              isActive ? "bg-yellow-200" : "bg-gray-200"
                            }`}
                          >
                            {place.placeType === 1 ? (
                              <Building
                                className={`w-6 h-6 ${isActive ? "text-yellow-500" : "text-gray-500"}`}
                              />
                            ) : (
                              <Hammer
                                className={`w-6 h-6 ${isActive ? "text-yellow-500" : "text-gray-500"}`}
                              />
                            )}
                          </div>
                          <p
                            className={`text-xs font-semibold text-center leading-tight min-h-[2rem] ${
                              isActive ? "text-yellow-600" : "text-gray-700"
                            }`}
                          >
                            {place.place}
                          </p>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Note */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Note (Optional)
              </label>
              <div className="flex items-start gap-3 bg-gray-50 border-2 border-gray-200 rounded-xl px-4 py-3 focus-within:border-yellow-400 transition">
                <FileText className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Add note"
                  rows={3}
                  maxLength={200}
                  className="flex-1 bg-transparent outline-none text-gray-900 resize-none"
                />
              </div>
            </div>
          </div>
        </div>

        {/* ── Footer ── */}
        <div className="border-t border-gray-100 px-5 py-4">
          <button
            onClick={handleUpdateExpense}
            disabled={isLoading}
            className={`w-full flex items-center justify-center gap-2 py-4 rounded-xl font-bold transition ${
              isLoading
                ? "bg-gray-300 text-gray-400 cursor-not-allowed"
                : "bg-yellow-400 hover:bg-yellow-500 text-white shadow-md"
            }`}
          >
            {isLoading ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>Updating...</span>
              </>
            ) : (
              <>
                <CheckCircle className="w-5 h-5" />
                <span>Update Expense</span>
              </>
            )}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes slide-up {
          from { transform: translateY(100%); }
          to   { transform: translateY(0); }
        }
        .animate-slide-up { animation: slide-up 0.3s ease-out; }
        @keyframes spin { to { transform: rotate(360deg); } }
        .animate-spin   { animation: spin 1s linear infinite; }
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
        input[type="number"]::-webkit-inner-spin-button,
        input[type="number"]::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }
        input[type="number"] { -moz-appearance: textfield; }
      `}</style>
    </div>
  );
}
